import axios, { AxiosInstance } from "axios";
import config from "../../../config";
import {
  RequesttoPayment,
  CreateRequesttoPaymentRequest,
  UpdateRequesttoPaymentRequest,
  ClerkPaymentDetailsRequest,
  ApprovalLevel,
  DashboardSummary,
  RequesttoPaymentAuditLog,
  RequesttoPaymentFilters,
  BulkApproveResponse,
  RtpSettings,
} from "../types/pettyCash.types";

class RequesttoPaymentService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: config.API_BASE_URL,
      headers: { "Content-Type": "application/json" },
      withCredentials: true, // send cookies so the server can refresh expired JWT tokens
    });

    this.api.interceptors.request.use((cfg) => {
      const token = localStorage.getItem("vms_access_token");
      if (token) cfg.headers.Authorization = `Bearer ${token}`;
      return cfg;
    });

    this.api.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 401) {
          localStorage.removeItem("vms_access_token");
          window.location.href = "/";
        }
        return Promise.reject(err);
      },
    );
  }

  async create(data: CreateRequesttoPaymentRequest): Promise<RequesttoPayment> {
    const r = await this.api.post<RequesttoPayment>("/petty-cash", data);
    return r.data;
  }

  async getList(
    filters?: RequesttoPaymentFilters,
  ): Promise<RequesttoPayment[]> {
    const r = await this.api.get<RequesttoPayment[]>("/petty-cash", {
      params: filters,
    });
    return r.data;
  }

  async getById(id: string): Promise<RequesttoPayment> {
    const r = await this.api.get<RequesttoPayment>(`/petty-cash/${id}`);
    return r.data;
  }

  async update(
    id: string,
    data: UpdateRequesttoPaymentRequest,
  ): Promise<RequesttoPayment> {
    const r = await this.api.patch<RequesttoPayment>(`/petty-cash/${id}`, data);
    return r.data;
  }

  async submit(id: string): Promise<RequesttoPayment> {
    const r = await this.api.post<RequesttoPayment>(`/petty-cash/${id}/submit`);
    return r.data;
  }

  async approve(
    id: string,
    approvalLevel: ApprovalLevel,
    comments?: string,
  ): Promise<RequesttoPayment> {
    const r = await this.api.post<RequesttoPayment>(
      `/petty-cash/${id}/approve`,
      {
        approvalLevel,
        comments,
      },
    );
    return r.data;
  }

  async bulkApprove(
    ids: string[],
    approvalLevel: ApprovalLevel,
    comments?: string,
  ): Promise<BulkApproveResponse> {
    const r = await this.api.post<BulkApproveResponse>(
      "/petty-cash/bulk-approve",
      {
        ids,
        approvalLevel,
        comments,
      },
    );
    return r.data;
  }

  async reject(
    id: string,
    approvalLevel: ApprovalLevel,
    reason: string,
  ): Promise<RequesttoPayment> {
    const r = await this.api.post<RequesttoPayment>(
      `/petty-cash/${id}/reject`,
      {
        approvalLevel,
        reason,
      },
    );
    return r.data;
  }

  async requestMoreInfo(
    id: string,
    approvalLevel: ApprovalLevel,
    message: string,
  ): Promise<RequesttoPayment> {
    const r = await this.api.post<RequesttoPayment>(
      `/petty-cash/${id}/more-info`,
      {
        approvalLevel,
        message,
      },
    );
    return r.data;
  }

  async cancel(id: string, reason: string): Promise<RequesttoPayment> {
    const r = await this.api.post<RequesttoPayment>(
      `/petty-cash/${id}/cancel`,
      {
        reason,
      },
    );
    return r.data;
  }

  async prepareForProcessing(id: string): Promise<RequesttoPayment> {
    const r = await this.api.post<RequesttoPayment>(
      `/petty-cash/${id}/prepare`,
    );
    return r.data;
  }

  async markPaid(
    id: string,
    payFrom: "CASH" | "BANK",
  ): Promise<RequesttoPayment> {
    const r = await this.api.post<RequesttoPayment>(
      `/petty-cash/${id}/mark-paid`,
      {
        payFrom,
      },
    );
    return r.data;
  }

  async updatePaymentDetails(
    id: string,
    data: ClerkPaymentDetailsRequest,
  ): Promise<RequesttoPayment> {
    const r = await this.api.patch<RequesttoPayment>(
      `/petty-cash/${id}/payment-details`,
      data,
    );
    return r.data;
  }

  async updateUrgency(
    id: string,
    urgent: boolean,
    urgentReason?: string,
  ): Promise<RequesttoPayment> {
    const r = await this.api.patch<RequesttoPayment>(`/petty-cash/${id}`, {
      urgent,
      urgentReason,
    });
    return r.data;
  }

  async getDashboardSummary(): Promise<DashboardSummary> {
    const r = await this.api.get<DashboardSummary>(
      "/petty-cash/summary/dashboard",
    );
    return r.data;
  }

  async getAuditLog(id: string): Promise<RequesttoPaymentAuditLog[]> {
    const r = await this.api.get<RequesttoPaymentAuditLog[]>(
      `/petty-cash/${id}/audit-log`,
    );
    return r.data;
  }

  async getSettings(): Promise<RtpSettings> {
    const r = await this.api.get<RtpSettings>("/petty-cash/summary/settings");
    return r.data;
  }

  async updateSettings(
    data: Partial<
      Pick<RtpSettings, "banks" | "ledgerCodes" | "currencies" | "projectCodes">
    >,
  ): Promise<RtpSettings> {
    const r = await this.api.patch<RtpSettings>(
      "/petty-cash/summary/settings",
      data,
    );
    return r.data;
  }
}

export default new RequesttoPaymentService();
